import { getApiBase, getToken } from '../../../lib/utils';
import type { AgentContact, ChatMessage } from '../types/messenger.types';

const apiBase = () => getApiBase();
const authHeaders = () => {
  const token = getToken();
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
};

export const messengerService = {
  async fetchAgents(): Promise<AgentContact[]> {
    const res = await fetch(`${apiBase()}/agents`, { headers: authHeaders() });
    if (!res.ok) throw new Error('Failed to fetch agents');
    const data = await res.json();
    const agents = Array.isArray(data) ? data : (data.items || data.agents || []);
    return agents.map((a: any): AgentContact => ({
      id: a.id,
      name: a.name,
      slug: a.slug,
      description: a.description ?? null,
      icon_url: a.icon_url ?? null,
      icon_preview_url: a.icon_preview_url ?? null,
      unreadCount: 0,
      isOnline: true,
    }));
  },

  async fetchLastMessages(): Promise<Record<number, { content: string; timestamp: string; isFromAgent: boolean }>> {
    try {
      const res = await fetch(`${apiBase()}/messenger/last-messages`, { headers: authHeaders() });
      if (!res.ok) return {};
      const data: Array<{
        agent_id: number;
        last_message: { content: string; timestamp: string; is_from_agent: boolean } | null;
        unread_count: number;
      }> = await res.json();

      const map: Record<number, { content: string; timestamp: string; isFromAgent: boolean }> = {};
      for (const item of data) {
        if (item.last_message) {
          map[item.agent_id] = {
            content: item.last_message.content,
            timestamp: item.last_message.timestamp,
            isFromAgent: item.last_message.is_from_agent,
          };
        }
      }
      return map;
    } catch {
      return {};
    }
  },

  async markRead(agentId: number): Promise<void> {
    try {
      await fetch(`${apiBase()}/messenger/mark-read/${agentId}`, {
        method: 'POST',
        headers: authHeaders(),
      });
    } catch { /* silencieux */ }
  },

  async fetchMessages(
    conversationId: number,
    options?: { limit?: number; beforeId?: number }
  ): Promise<{ messages: ChatMessage[]; hasMore: boolean }> {
    const params = new URLSearchParams();
    if (options?.limit) params.set('limit', String(options.limit));
    if (options?.beforeId) params.set('before_id', String(options.beforeId));
    const qs = params.toString() ? `?${params.toString()}` : '';

    const res = await fetch(
      `${apiBase()}/conversations/${conversationId}/messages${qs}`,
      { headers: authHeaders() }
    );
    if (!res.ok) throw new Error('Failed to fetch messages');
    const data = await res.json();
    const msgs = (data.messages || [])
      .filter((m: any) => (m.role === 'user' || m.role === 'assistant') && m.content)
      .map((m: any, i: number): ChatMessage => ({
        id: String(m.id ?? i),
        content: m.content,
        timestamp: m.created_at ?? new Date().toISOString(),
        isFromAgent: m.role === 'assistant',
      }));
    return { messages: msgs, hasMore: data.has_more ?? false };
  },

  async fetchAllConversationIds(agentId: number): Promise<number[]> {
    const res = await fetch(
      `${apiBase()}/conversations?agent_id=${agentId}&limit=200&sort_by=created_at&sort_order=asc`,
      { headers: authHeaders() }
    );
    if (!res.ok) return [];
    const data = await res.json();
    const list = Array.isArray(data) ? data : (data.items || data.conversations || []);
    return list.map((c: any) => c.id);
  },

  async fetchLastConversationId(agentId: number): Promise<number | null> {
    const res = await fetch(
      `${apiBase()}/conversations?agent_id=${agentId}&limit=1&sort_by=updated_at&sort_order=desc`,
      { headers: authHeaders() }
    );
    if (!res.ok) return null;
    const data = await res.json();
    const list = Array.isArray(data) ? data : (data.items || data.conversations || []);
    return list.length > 0 ? list[0].id : null;
  },

  streamMessage(
    agentId: number,
    question: string,
    conversationId: number | null,
    callbacks: {
      onToken: (token: string) => void;
      onDone: (conversationId: number | null) => void;
      onError: (err: string) => void;
      onStatus?: (msg: string) => void;
    }
  ): () => void {
    const controller = new AbortController();

    (async () => {
      try {
        const body: any = {
          question,
          top_k: 5,
          with_sources: false,
          agent_id: agentId,
          source: 'widget_internal',
          conversation_id: conversationId,
        };

        const resp = await fetch(`${apiBase()}/chat/stream`, {
          method: 'POST',
          headers: authHeaders(),
          body: JSON.stringify(body),
          signal: controller.signal,
        });

        if (!resp.ok) {
          const err = await resp.json().catch(() => ({}));
          callbacks.onError(err.detail || `HTTP ${resp.status}`);
          return;
        }

        const reader = resp.body!.getReader();
        const decoder = new TextDecoder();
        let buffer = '';
        let newConversationId: number | null = conversationId;

        while (true) {
          const { value, done } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n\n');
          buffer = lines.pop() || '';

          for (const line of lines) {
            if (!line.startsWith('data: ')) continue;
            try {
              const data = JSON.parse(line.slice(6));
              if (data.error) { callbacks.onError(data.error); return; }
              if (data.status_message) callbacks.onStatus?.(data.status_message);
              if (data.token) callbacks.onToken(data.token);
              if (data.conversation_id) newConversationId = data.conversation_id;
            } catch { /* skip malformed */ }
          }
        }

        callbacks.onDone(newConversationId);
      } catch (e: any) {
        if (e?.name !== 'AbortError') callbacks.onError(e?.message || 'Erreur réseau');
      }
    })();

    return () => controller.abort();
  },
};
